import axios, { type AxiosError, type AxiosInstance } from 'axios';

import { serverCode } from './api-errors';

import { useAuthStore } from '@/stores/auth';

// Chemins relatifs à l'origine de l'UI : /platform/* est proxifié vers platform-api (Vite en dev, Nginx en prod)
export const API_BASE_PATH = '/platform';

// Un 401 sur ces routes est un refus d'identifiants ou de code, affiché par le formulaire : pas une session expirée
// (sinon un login raté nettoierait la session et redirigerait). Liste explicite, cf. TD-016 (intercepteur ui-shared).
const AUTH_FLOW_ENDPOINT = /^(\/platform)?\/?auth\/(login|mfa\/verify)\/?(\?|$)/;

export const isAuthFlowEndpoint = (url: string | undefined): boolean => AUTH_FLOW_ENDPOINT.test(url ?? '');

// Codes des 401 « re-authentification ratée » sur une session valide (mauvais mot de passe actuel, mauvais code MFA) :
// le formulaire les affiche, la session reste. Tout autre 401 (sans code, UNAUTHORIZED) = session expirée.
export const KEEP_SESSION_ERROR_CODES: readonly string[] = ['INVALID_CURRENT_PASSWORD', 'INVALID_MFA_CODE'];

const keepsSession = (error: AxiosError): boolean => {
  const code = serverCode(error);
  return code !== undefined && KEEP_SESSION_ERROR_CODES.includes(code);
};

// Branché par l'app sur le routeur (redirection /login) ; le client ne connaît pas le routeur
let onSessionExpired: (() => void) | null = null;
export const setSessionExpiredHandler = (handler: (() => void) | null): void => {
  onSessionExpired = handler;
};

export const createApiClient = (): AxiosInstance => {
  const client = axios.create({ baseURL: API_BASE_PATH, timeout: 15_000 });

  client.interceptors.request.use((config) => {
    const token = useAuthStore.getState().accessToken;
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  // Pas de refresh token (9.2) : un 401 = jeton expiré ou admin désactivé → re-login simple, sans nouvel essai
  client.interceptors.response.use(
    (r) => r,
    (error: AxiosError) => {
      if (error.response?.status === 401 && !isAuthFlowEndpoint(error.config?.url) && !keepsSession(error)) {
        useAuthStore.getState().clearAuth({ expired: true });
        onSessionExpired?.();
      }
      throw error;
    },
  );

  return client;
};

export const api = createApiClient();
